import React, { useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { translations } from '../i18n';
import LanguageSelector from './LanguageSelector';

const PATIENT_FILE_PREFERENCE_OPTIONS = ['full_language', 'abbreviations', 'no_preference', 'other'];
const EXCLUDED_CLINICAL_DOMAIN_OPTIONS = ['pediatric_dentistry', 'periodontology', 'oral_surgery', 'endodontics', 'orthodontics', 'none'];

const FIELD_ERROR_ORDER = [
  'email',
  'password',
  'confirmPassword',
  'practitionerName',
  'clinicName',
  'clinicAddress',
  'clinicPostalCode',
  'clinicCity',
  'clinicPhone',
  'clinicSize',
  'patientFilePreference',
  'patientFilePreferenceOther',
  'excludedClinicalDomains'
];

const FIELD_SCROLL_TARGETS = {
  clinicSize: 'clinicSizeSection',
  patientFilePreference: 'patientFilePreferenceSection',
  patientFilePreferenceOther: 'patientFilePreferenceSection',
  excludedClinicalDomains: 'excludedClinicalDomainsSection'
};

const scrollToFirstError = (validationErrors) => {
  const firstErrorField = FIELD_ERROR_ORDER.find((field) => validationErrors[field]);

  requestAnimationFrame(() => {
    if (firstErrorField) {
      const targetId = FIELD_SCROLL_TARGETS[firstErrorField] || firstErrorField;
      const element = document.getElementById(targetId);
      if (!element) {
        return;
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const focusable = element.querySelector('input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusable?.focus) {
        focusable.focus({ preventScroll: true });
      }
      return;
    }

    if (validationErrors.submit) {
      document.getElementById('submitSection')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
};

const RegistrationForm = ({ onSuccess, currentLanguage, onLanguageChange }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    practitionerName: '',
    clinicName: '',
    clinicAddress: '',
    clinicCity: '',
    clinicPostalCode: '',
    clinicPhone: '',
    clinicSize: '',
    demoPreference: false,
    patientFilePreference: '',
    patientFilePreferenceOther: '',
    excludedClinicalDomains: [],
    language: currentLanguage
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const t = translations[currentLanguage];

  // Update language in form data when language changes
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      language: currentLanguage
    }));
  }, [currentLanguage]);

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '48px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#667eea',
      },
      borderColor: state.isFocused ? '#667eea' : errors.clinicSize ? '#ef4444' : '#e5e7eb',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 12px',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      padding: '0 12px',
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '8px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      border: '1px solid #e5e7eb',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#667eea' 
        : state.isFocused 
          ? '#f3f4f6' 
          : 'white',
      color: state.isSelected ? 'white' : '#374151',
      padding: '12px 16px',
      fontSize: '16px',
      fontWeight: '500',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#374151',
      fontSize: '16px',
      fontWeight: '500',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
      fontSize: '16px',
    }),
  };

  const getValidationErrors = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = t.required;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t.emailInvalid;
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t.required;
    } else {
      const password = formData.password;
      const hasMinLength = password.length >= 8;
      const hasUpperCase = /[A-Z]/.test(password);
      const hasLowerCase = /[a-z]/.test(password);
      const hasNumber = /[0-9]/.test(password);
      const hasSpecialChar = /[!@#$%^&*]/.test(password);
      
      if (!hasMinLength) {
        newErrors.password = t.passwordMinLength;
      } else if (!hasUpperCase) {
        newErrors.password = t.passwordNoUpperCase;
      } else if (!hasLowerCase) {
        newErrors.password = t.passwordNoLowerCase;
      } else if (!hasNumber) {
        newErrors.password = t.passwordNoNumber;
      } else if (!hasSpecialChar) {
        newErrors.password = t.passwordNoSpecialChar;
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t.required;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t.passwordsNotMatch;
    }

    // Practitioner name validation
    if (!formData.practitionerName.trim()) {
      newErrors.practitionerName = t.required;
    }

    // Clinic name validation
    if (!formData.clinicName.trim()) {
      newErrors.clinicName = t.required;
    }

    // Clinic address validation
    if (!formData.clinicAddress.trim()) {
      newErrors.clinicAddress = t.required;
    }

    // Clinic city validation
    if (!formData.clinicCity.trim()) {
      newErrors.clinicCity = t.required;
    }

    // Postal code validation
    if (!formData.clinicPostalCode.trim()) {
      newErrors.clinicPostalCode = t.required;
    } else if (!/^\d{4}$/.test(formData.clinicPostalCode)) {
      newErrors.clinicPostalCode = t.postalCodeInvalid;
    }

    // Phone validation
    if (!formData.clinicPhone.trim()) {
      newErrors.clinicPhone = t.required;
    }

    // Clinic size validation
    if (!formData.clinicSize) {
      newErrors.clinicSize = t.required;
    }

    if (!formData.patientFilePreference) {
      newErrors.patientFilePreference = t.required;
    } else if (formData.patientFilePreference === 'other' && !formData.patientFilePreferenceOther.trim()) {
      newErrors.patientFilePreferenceOther = t.patientFilePreferenceOtherRequired;
    }

    if (formData.excludedClinicalDomains.length === 0) {
      newErrors.excludedClinicalDomains = t.excludedDomainsRequired;
    } else {
      const hasNone = formData.excludedClinicalDomains.includes('none');
      const hasDomains = formData.excludedClinicalDomains.some(domain => domain !== 'none');
      if (hasNone && hasDomains) {
        newErrors.excludedClinicalDomains = t.excludedDomainsConflict;
      }
    }

    return newErrors;
  };

  const showValidationErrors = (validationErrors) => {
    setErrors(validationErrors);
    scrollToFirstError(validationErrors);
  };

  const hasFormErrors = Object.values(errors).some(Boolean);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear errors when user starts typing
    if (errors[name] || errors.submit) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
        submit: '' // Also clear general submit errors
      }));
    }
  };

  const handlePatientFilePreferenceChange = (value) => {
    setFormData(prev => ({
      ...prev,
      patientFilePreference: value,
      patientFilePreferenceOther: value === 'other' ? prev.patientFilePreferenceOther : ''
    }));

    if (errors.patientFilePreference || errors.patientFilePreferenceOther || errors.submit) {
      setErrors(prev => ({
        ...prev,
        patientFilePreference: '',
        patientFilePreferenceOther: '',
        submit: ''
      }));
    }
  };

  const handleExcludedDomainChange = (domain, checked) => {
    setFormData(prev => {
      let nextDomains = [...prev.excludedClinicalDomains];

      if (domain === 'none') {
        nextDomains = checked ? ['none'] : [];
      } else if (checked) {
        nextDomains = [...nextDomains.filter(item => item !== 'none'), domain];
      } else {
        nextDomains = nextDomains.filter(item => item !== domain);
      }

      return {
        ...prev,
        excludedClinicalDomains: nextDomains
      };
    });

    if (errors.excludedClinicalDomains || errors.submit) {
      setErrors(prev => ({
        ...prev,
        excludedClinicalDomains: '',
        submit: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = getValidationErrors();
    if (Object.keys(validationErrors).length > 0) {
      showValidationErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const { confirmPassword, ...registrationPayload } = formData;
      const response = await axios.post(`${apiBaseUrl}/api/registration`, registrationPayload);
      
      if (response.status === 201) {
        onSuccess();
      }
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      if (error.response?.status === 400) {
        // Handle validation errors from server
        const serverErrors = error.response.data.errors || {};
        const serverError = error.response.data.error;
        console.log('Server errors:', serverErrors);
        console.log('Server error:', serverError);
        
        // Translate error messages if they exist
        const translatedErrors = {};
        let hasEmailError = false;
        
        // Check for email error in both errors object and main error field
        if (serverError === 'EMAIL_ALREADY_EXISTS' || serverErrors.email === 'EMAIL_ALREADY_USED') {
          translatedErrors.email = t.emailAlreadyUsed;
          translatedErrors.submit = t.emailAlreadyExists;
          hasEmailError = true;
          console.log('Email error detected, setting translated messages');
        } else {
          // Handle other field errors
          Object.keys(serverErrors).forEach(key => {
            console.log(`Processing error key: ${key}, value: ${serverErrors[key]}`);
            translatedErrors[key] = serverErrors[key];
          });
        }
        
        console.log('Final translated errors:', translatedErrors);
        showValidationErrors(translatedErrors);
      } else {
        // Handle other errors
        console.log('Setting general submit error:', t.submitError);
        showValidationErrors({
          submit: t.submitError
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-container">
      <LanguageSelector 
        currentLanguage={currentLanguage} 
        onLanguageChange={onLanguageChange} 
      />
          <h1 className="form-title">{t.title}</h1>
          <p className="form-subtitle">
            <strong>{t.subtitle}</strong>
          </p>


          <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              {t.email} <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder={t.emailPlaceholder}
            />
            {errors.email && <div className="error-message">{errors.email}</div>}
          </div>
        </div>

        <div className="form-row password-row">
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              {t.password} <span className="required">*</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`form-input ${errors.password ? 'error' : ''}`}
              placeholder={t.passwordPlaceholder}
            />
            {errors.password && <div className="error-message">{errors.password}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              {t.confirmPassword} <span className="required">*</span>
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
              placeholder={t.confirmPasswordPlaceholder}
            />
            {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="practitionerName" className="form-label">
              {t.practitionerName} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="practitionerName"
              name="practitionerName"
              value={formData.practitionerName}
              onChange={handleInputChange}
              className={`form-input ${errors.practitionerName ? 'error' : ''}`}
              placeholder={t.practitionerNamePlaceholder}
            />
            {errors.practitionerName && <div className="error-message">{errors.practitionerName}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="clinicName" className="form-label">
              {t.clinicName} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="clinicName"
              name="clinicName"
              value={formData.clinicName}
              onChange={handleInputChange}
              className={`form-input ${errors.clinicName ? 'error' : ''}`}
              placeholder={t.clinicNamePlaceholder}
            />
            {errors.clinicName && <div className="error-message">{errors.clinicName}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="clinicAddress" className="form-label">
              {t.clinicAddress} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="clinicAddress"
              name="clinicAddress"
              value={formData.clinicAddress}
              onChange={handleInputChange}
              className={`form-input ${errors.clinicAddress ? 'error' : ''}`}
              placeholder={t.clinicAddressPlaceholder}
            />
            {errors.clinicAddress && <div className="error-message">{errors.clinicAddress}</div>}
          </div>
        </div>

        <div className="form-row postal-city-row">
          <div className="form-group postal-code-group">
            <label htmlFor="clinicPostalCode" className="form-label">
              {t.clinicPostalCode} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="clinicPostalCode"
              name="clinicPostalCode"
              value={formData.clinicPostalCode}
              onChange={handleInputChange}
              className={`form-input ${errors.clinicPostalCode ? 'error' : ''}`}
              placeholder={t.clinicPostalCodePlaceholder}
              maxLength="4"
            />
            {errors.clinicPostalCode && <div className="error-message">{errors.clinicPostalCode}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="clinicCity" className="form-label">
              {t.clinicCity} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="clinicCity"
              name="clinicCity"
              value={formData.clinicCity}
              onChange={handleInputChange}
              className={`form-input ${errors.clinicCity ? 'error' : ''}`}
              placeholder={t.clinicCityPlaceholder}
            />
            {errors.clinicCity && <div className="error-message">{errors.clinicCity}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="clinicPhone" className="form-label">
              {t.clinicPhone} <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="clinicPhone"
              name="clinicPhone"
              value={formData.clinicPhone}
              onChange={handleInputChange}
              className={`form-input ${errors.clinicPhone ? 'error' : ''}`}
              placeholder={t.clinicPhonePlaceholder}
            />
            {errors.clinicPhone && <div className="error-message">{errors.clinicPhone}</div>}
          </div>
        </div>

        <div className="form-row" id="clinicSizeSection">
          <div className="form-group">
            <label htmlFor="clinicSize" className="form-label">
              {t.clinicSize} <span className="required">*</span>
            </label>
            <Select
              inputId="clinicSize"
              value={formData.clinicSize ? { value: formData.clinicSize, label: t.clinicSizeOptions[formData.clinicSize] } : null}
              onChange={(option) => setFormData({ ...formData, clinicSize: option ? option.value : '' })}
              options={[
                { value: '1', label: t.clinicSizeOptions['1'] },
                { value: '2-5', label: t.clinicSizeOptions['2-5'] },
                { value: '6-15', label: t.clinicSizeOptions['6-15'] }
              ]}
              styles={customSelectStyles}
              isSearchable={false}
              placeholder={t.clinicSize}
              className="clinic-size-select"
              classNamePrefix="clinic-size-select"
            />
            {errors.clinicSize && <div className="error-message">{errors.clinicSize}</div>}
          </div>
        </div>

        <div className="form-checkbox">
          <input
            type="checkbox"
            id="demoPreference"
            name="demoPreference"
            checked={formData.demoPreference}
            onChange={handleInputChange}
          />
          <label htmlFor="demoPreference">
            {t.demoPreference}
          </label>
        </div>

        <div
          id="patientFilePreferenceSection"
          className={`form-question-section ${errors.patientFilePreference || errors.patientFilePreferenceOther ? 'error' : ''}`}
        >
          <h3 className="form-question-title">
            {t.patientFilePreferenceTitle} <span className="required">*</span>
          </h3>
          <p className="form-question-text">{t.patientFilePreferenceQuestion}</p>
          <div className="form-choice-group">
            {PATIENT_FILE_PREFERENCE_OPTIONS.map((option) => (
              <label key={option} className="form-choice-item">
                <input
                  type="radio"
                  name="patientFilePreference"
                  value={option}
                  checked={formData.patientFilePreference === option}
                  onChange={() => handlePatientFilePreferenceChange(option)}
                />
                <span>{t.patientFilePreferenceOptions[option]}</span>
              </label>
            ))}
          </div>
          {formData.patientFilePreference === 'other' && (
            <div className="form-group form-other-input">
              <input
                type="text"
                id="patientFilePreferenceOther"
                name="patientFilePreferenceOther"
                value={formData.patientFilePreferenceOther}
                onChange={handleInputChange}
                className={`form-input ${errors.patientFilePreferenceOther ? 'error' : ''}`}
                placeholder={t.patientFilePreferenceOtherPlaceholder}
              />
              {errors.patientFilePreferenceOther && (
                <div className="error-message">{errors.patientFilePreferenceOther}</div>
              )}
            </div>
          )}
          {errors.patientFilePreference && (
            <div className="error-message">{errors.patientFilePreference}</div>
          )}
        </div>

        <div
          id="excludedClinicalDomainsSection"
          className={`form-question-section ${errors.excludedClinicalDomains ? 'error' : ''}`}
        >
          <h3 className="form-question-title">
            {t.excludedDomainsTitle} <span className="required">*</span>
          </h3>
          <p className="form-question-text">{t.excludedDomainsQuestion}</p>
          <div className="form-choice-group">
            {EXCLUDED_CLINICAL_DOMAIN_OPTIONS.map((domain) => (
              <label key={domain} className="form-choice-item">
                <input
                  type="checkbox"
                  name="excludedClinicalDomains"
                  value={domain}
                  checked={formData.excludedClinicalDomains.includes(domain)}
                  onChange={(e) => handleExcludedDomainChange(domain, e.target.checked)}
                />
                <span>{t.excludedClinicalDomainOptions[domain]}</span>
              </label>
            ))}
          </div>
          {errors.excludedClinicalDomains && (
            <div className="error-message">{errors.excludedClinicalDomains}</div>
          )}
        </div>

        <div id="submitSection" className={`submit-section ${hasFormErrors ? 'has-errors' : ''}`}>
          {hasFormErrors && (
            <div className="submit-error-panel" role="alert" aria-live="polite">
              <span className="error-icon" aria-hidden="true">⚠️</span>
              <div className="submit-error-panel-content">
                <strong className="submit-error-panel-title">{t.formValidationTitle}</strong>
                <p className="submit-error-panel-text">
                  {errors.submit || t.formValidationSummary}
                </p>
              </div>
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting && <span className="loading"></span>}
            {isSubmitting ? t.submitting : t.submit}
          </button>
        </div>
      </form>

      <div className="help-section">
        <div className="help-title">{t.helpTitle}</div>
        <div className="help-text">
          {t.helpDescription}
        </div>
        <div className="help-text">
          📧 <a href={`mailto:${t.helpEmail}`} className="help-contact">{t.helpEmail}</a>
        </div>
        <div className="help-text">
          📞 <a href={`tel:+41764081208`} className="help-contact">{t.helpPhone}</a>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
